import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import FiguredLandingHelmet from 'common/helmets/FiguredLandingHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import UppercaseHeader from 'common/UppercaseHeader';
import FiguredWordmark from 'img/landing/figured-wordmark.webp';
import Nick from 'img/landing/nick.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const FiguredLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Figured Case Study');
  }, [trackEvent]);

  return (
    <div className="landingFigured landingCustomersPage">
      <FiguredLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <div className="text">
            <UppercaseHeader>Customer story</UppercaseHeader>
            <h1>
              How Figured uses Canny to strengthen team collaboration and build&nbsp;community
            </h1>
            <Link className="cta" to="/register">
              <Button buttonType="greenButton" value="Get Started" />
            </Link>
          </div>
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Nick Barraclough</div>
              <div className="title">Partner Success Manager at&nbsp;Figured</div>
            </div>
            <LazyLoadedImage loading="eager" src={Nick} alt="Nick Barraclough" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              When people think of farming, they usually think tractors and cows. What most don’t
              realize is that many farms are multi-million dollar businesses with incredibly
              complicated&nbsp;structures.
            </p>
            <p>
              Most farmers, however, don’t have complete financial visibility into their business.
              This means that budgeting and long term business planning can be&nbsp;complicated.
            </p>
            <p>
              Figured helps drive farm profitability by working with trusted partners—like
              accountants, farm consultants, and bankers—to link together important financial data,
              make it visible, and bring the whole farming team onto the same&nbsp;page.
            </p>
            <p>
              Nick Barraclough is Partner Success Manager at Figured, working with the customer team
              to make sure their clients, consultants, and partners make the most out of
              the&nbsp;software.
            </p>
            <p>
              His team also works closely with the product team, acting as the advocate for people
              using the&nbsp;tool.
            </p>
            <p className="quote">
              &ldquo;There needs to be a healthy tension between the customer team and the product
              team. We have to inform them about issues and push on it, but also pass on the
              good&nbsp;feedback.&rdquo;
            </p>
            <p>
              Besides communicating user needs to the product team, Nick and the customer team also
              work with the development team, making sure everyone is on the same page about
              feature&nbsp;requirements.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Before Canny, Figured struggled with a different tool to manage their feedback and
              feature&nbsp;requests.
            </p>
            <p className="quote">
              &ldquo;It wasn’t pretty. That’s the first reason we started looking elsewhere. We put
              a lot of effort into our own user experience, and if our own product isn’t good,
              people won’t use&nbsp;it.&rdquo;
            </p>
            <p>
              Although the customer team tried their best to direct customers to leave feedback, the
              poor experience meant that engagement was close to&nbsp;zero.
            </p>
            <p>
              The feedback that would roll in through other channels would sit in team members’
              inboxes or Slack channels, and die a quiet&nbsp;death.
            </p>
            <p>
              The user experience wasn’t only poor on the customers’ side. The Figured team
              themselves weren’t too happy about it,&nbsp;either.
            </p>
            <p className="quote">
              &ldquo;There were some half-finished ideas in there, but nobody wanted to go through
              them to add more detail because it was so tough to&nbsp;use.&rdquo;
            </p>
            <p>Another issue was disconnection.</p>
            <p>
              The previous tool the team used didn’t integrate with any of theirs—it was a
              completely separate island.
            </p>
            <p>
              They weren’t always aware if requests were coming in or not, and there was little to
              no conversation happening.
            </p>
            <p>
              After one too many suggestions slipped through the cracks, the team decided that they
              were wasting too much money on a tool that was not being used.
            </p>
            <p className="quote">
              &ldquo;We encourage everyone in the company to make bold decisions. We have a very
              driven and productive team. If something doesn’t work for us, we&nbsp;switch.&rdquo;
            </p>
            <p>The requirements for a fresh new feedback management tool were:</p>
            <ul>
              <li>
                A clean, easy-to-use interface that provides a great user experience for both the
                team and their&nbsp;customers
              </li>
              <li>
                The ability to integrate with their other channels, so nothing falls through
                the&nbsp;cracks
              </li>
            </ul>
            <p className="quote">
              &ldquo;We don’t need a million complicated features, we just need it to do what it
              does really well, and look beautiful while doing&nbsp;it.&rdquo;
            </p>
            <p>So, the search&nbsp;began.</p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              After some digging around, Jessie—Customer Success Specialist at Figured—stumbled
              upon&nbsp;Canny.
            </p>
            <p className="quote">
              &ldquo;She said: “This is pretty, I’ll look into it.” She loved it, we compared it to
              what we were using then, and never looked&nbsp;back.&rdquo;
            </p>
            <p>
              The team setting Canny up was also very happy about the migration process from one
              tool to&nbsp;another.
            </p>
            <p className="quote">
              &ldquo;It couldn’t have been easier, I wish more tools were this straightforward to
              set&nbsp;up.&rdquo;
            </p>
            <p>
              After all existing feedback was successfully migrated, the team got excited about
              combing through the&nbsp;requests.
            </p>
            <p>
              With a tidier and better-looking tool, a process that would have been incredibly
              laborious before, was now a&nbsp;breeze.
            </p>
            <p>Canny also resolved the disconnect between channels and other&nbsp;teams.</p>
            <p className="quote">
              &ldquo;Suddenly, everything from Canny to Intercom to Slack was linked. We were
              actually able to record what people wanted in one place, with basically
              no&nbsp;effort.&rdquo;
            </p>
            <p>
              Even though Canny is predominantly owned by the customer team, nearly everyone—from
              customer success to the CEO—spends time in&nbsp;it.
            </p>
            <p>
              The whole team can do and see things quickly, and is connected to users and
              their&nbsp;requests.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              One of the main benefits of using Canny has been the excitement and sense of
              collaboration that the team now&nbsp;has.
            </p>
            <p className="quote">
              &ldquo;As soon as something blows up in Canny, everyone knows. It’s all connected and
              accessible to&nbsp;everyone.&rdquo;
            </p>
            <p>
              Nick noticed that team members started proactively going to Canny to see what their
              customers want. Conversations have started to happen unprompted, and everyone is
              effortlessly on the same&nbsp;page.
            </p>
            <p>
              Besides internal clarity, another big bonus was the communication that opened up
              between Figured and its&nbsp;users.
            </p>
            <p>When customers see other requests, it begins a conversation&nbsp;organically.</p>
            <p className="quote">&ldquo;It’s almost become like a mini&nbsp;community.&rdquo;</p>
            <p>And, their users are no longer confused or waiting in the&nbsp;dark.</p>
            <p className="quote">
              &ldquo;I’ve heard our customers say some great things about Canny. What I haven’t
              heard is, ‘When are you going to start working on this?’ or ‘Have you not gotten
              my&nbsp;feedback?’&rdquo;
            </p>
            <p>
              Ultimately, Canny was exactly what the team at Figured was looking for. And as for
              whether it could be the right fit for someone else,&nbsp;too...
            </p>
            <p className="quote">
              &ldquo;Anyone who spends any time looking at other tools—and we did—is just being
              daft. You’d be crazy not to go for&nbsp;Canny.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Figured Wordmark"
              className="figuredWordmark"
              src={FiguredWordmark}
            />
            Figured is a complete farm tracking, management, and forecasting tool that provides
            accurate data in one place, in real&nbsp;time.
            <a href="https://www.figured.com/" rel="noopener" target="_blank">
              figured.com
            </a>
            <div className="stat">
              <UppercaseHeader>Customers</UppercaseHeader>
              <p>25,000+ farms across the&nbsp;world</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>Global</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>2</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>3,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default FiguredLanding;
